import { Text } from '@payaca/ambrosia';
import type { AddressCoordinates } from '@payaca/helpers/locationHelper';
import GoogleMapReact from 'google-map-react';
import { FC, useEffect, useRef, useState } from 'react';

import { getDraggedPosition, getLookupGhost } from './siteLocationEditorState';
import {
  getSitePinDataUri,
  LOOKUP_PIN_COLOUR,
  SITE_PIN_COLOUR,
  SITE_PIN_ICON_ANCHOR,
  SITE_PIN_ICON_SIZE,
} from './siteLocationMarkerArt';

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;
const CONNECTOR_OFF_WHITE = '#fff8e7';

const ACTIVE_MARKER_ICON = getSitePinDataUri({
  active: true,
  colour: SITE_PIN_COLOUR,
});
const LOOKUP_MARKER_ICON = getSitePinDataUri({
  colour: LOOKUP_PIN_COLOUR,
  fill: 'white',
});

type MapLoadStatus = 'loading' | 'ready' | 'error';

type MapLatLng = {
  lat(): number;
  lng(): number;
};

type MapInstance = object;

type MapPoint = { lat: number; lng: number };
type MarkerEvent = { latLng?: MapLatLng | null };
type MapListener = { remove?: () => void };
type MarkerDragState = {
  originalPosition: AddressCoordinates;
  pointerPosition: AddressCoordinates | null;
};

type MarkerInstance = {
  addListener: (
    eventName: string,
    callback: (event?: MarkerEvent) => void
  ) => MapListener;
  getPosition: () => MapLatLng | null;
  setDraggable: (draggable: boolean) => void;
  setMap: (map: MapInstance | null) => void;
  setPosition: (position: MapPoint) => void;
};

type PolylineSymbol = {
  path: string;
  scale: number;
  strokeColor: string;
  strokeOpacity: number;
  strokeWeight: number;
};

type PolylineInstance = {
  setMap: (map: MapInstance | null) => void;
  setPath: (path: MapPoint[]) => void;
};

type GoogleMapsApi = {
  Marker: new (options: {
    clickable?: boolean;
    draggable?: boolean;
    icon?: {
      anchor: unknown;
      scaledSize: unknown;
      url: string;
    };
    map: MapInstance;
    opacity?: number;
    position: MapPoint;
    title?: string;
    zIndex?: number;
  }) => MarkerInstance;
  Point: new (x: number, y: number) => unknown;
  Polyline: new (options: {
    geodesic: boolean;
    icons: Array<{
      icon: PolylineSymbol;
      offset: string;
      repeat: string;
    }>;
    map: MapInstance;
    path: MapPoint[];
    strokeColor: string;
    strokeOpacity: number;
    strokeWeight: number;
  }) => PolylineInstance;
  Size: new (width: number, height: number) => unknown;
};

type Props = {
  centre: AddressCoordinates;
  disabled: boolean;
  editable: boolean;
  lookupPin: AddressCoordinates | null;
  onPositionChange: (coordinates: AddressCoordinates) => void;
  pin: AddressCoordinates | null;
};

const SiteLocationEditorMap: FC<Props> = (props) => {
  const { centre, disabled, editable, lookupPin, onPositionChange, pin } =
    props;
  const mapRef = useRef<MapInstance | null>(null);
  const mapsRef = useRef<GoogleMapsApi | null>(null);
  const activeMarkerRef = useRef<MarkerInstance | null>(null);
  const activeMarkerListenersRef = useRef<MapListener[]>([]);
  const activeMarkerDragRef = useRef<MarkerDragState | null>(null);
  const lookupMarkerRef = useRef<MarkerInstance | null>(null);
  const connectorRef = useRef<PolylineInstance | null>(null);
  const disabledRef = useRef(disabled);
  const editableRef = useRef(editable);
  const onPositionChangeRef = useRef(onPositionChange);
  const [mapLoadStatus, setMapLoadStatus] = useState<MapLoadStatus>('loading');

  disabledRef.current = disabled;
  editableRef.current = editable;
  onPositionChangeRef.current = onPositionChange;

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    if (!pin) {
      activeMarkerListenersRef.current.forEach((listener) =>
        listener.remove?.()
      );
      activeMarkerListenersRef.current = [];
      activeMarkerDragRef.current = null;
      activeMarkerRef.current?.setMap(null);
      activeMarkerRef.current = null;
      return;
    }

    if (activeMarkerRef.current) {
      activeMarkerDragRef.current = null;
      activeMarkerRef.current.setMap(map);
      activeMarkerRef.current.setPosition({
        lat: pin.latitude,
        lng: pin.longitude,
      });
      activeMarkerRef.current.setDraggable(editable && !disabled);
      return;
    }

    const marker = new maps.Marker({
      draggable: editable && !disabled,
      icon: {
        url: ACTIVE_MARKER_ICON,
        scaledSize: new maps.Size(
          SITE_PIN_ICON_SIZE.width,
          SITE_PIN_ICON_SIZE.height
        ),
        anchor: new maps.Point(SITE_PIN_ICON_ANCHOR.x, SITE_PIN_ICON_ANCHOR.y),
      },
      map,
      position: { lat: pin.latitude, lng: pin.longitude },
      title: 'Site pin',
      zIndex: 2,
    });
    const getCoordinates = (
      position: MapLatLng | null | undefined
    ): AddressCoordinates | null => {
      if (!position) return null;

      const latitude = position.lat();
      const longitude = position.lng();
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }

      return { latitude, longitude };
    };
    const setMarkerPosition = (position: AddressCoordinates) => {
      marker.setPosition({
        lat: position.latitude,
        lng: position.longitude,
      });
    };
    const dragStartListener = marker.addListener('dragstart', (event) => {
      const originalPosition = getCoordinates(marker.getPosition());
      if (!originalPosition) {
        activeMarkerDragRef.current = null;
        return;
      }

      activeMarkerDragRef.current = {
        originalPosition,
        pointerPosition: getCoordinates(event?.latLng),
      };
    });
    const dragEndListener = marker.addListener('dragend', (event) => {
      const dragState = activeMarkerDragRef.current;
      activeMarkerDragRef.current = null;
      if (!dragState) return;

      const position = getDraggedPosition(
        dragState.originalPosition,
        dragState.pointerPosition,
        getCoordinates(event?.latLng)
      );
      if (!position) {
        setMarkerPosition(dragState.originalPosition);
        return;
      }

      setMarkerPosition(position);
      if (!editableRef.current || disabledRef.current) return;
      onPositionChangeRef.current(position);
    });

    activeMarkerRef.current = marker;
    activeMarkerListenersRef.current = [dragStartListener, dragEndListener];
  }, [disabled, editable, mapLoadStatus, pin]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (!map || !maps) return;

    if (!lookupPin || !getLookupGhost(pin, lookupPin)) {
      lookupMarkerRef.current?.setMap(null);
      lookupMarkerRef.current = null;
      return;
    }

    if (lookupMarkerRef.current) {
      lookupMarkerRef.current.setMap(map);
      lookupMarkerRef.current.setPosition({
        lat: lookupPin.latitude,
        lng: lookupPin.longitude,
      });
      return;
    }

    const marker = new maps.Marker({
      clickable: false,
      icon: {
        url: LOOKUP_MARKER_ICON,
        scaledSize: new maps.Size(
          SITE_PIN_ICON_SIZE.width,
          SITE_PIN_ICON_SIZE.height
        ),
        anchor: new maps.Point(SITE_PIN_ICON_ANCHOR.x, SITE_PIN_ICON_ANCHOR.y),
      },
      map,
      opacity: 0.8,
      position: { lat: lookupPin.latitude, lng: lookupPin.longitude },
      title: 'Address lookup position',
      zIndex: 1,
    });

    lookupMarkerRef.current = marker;
  }, [lookupPin, mapLoadStatus, pin]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    if (
      !map ||
      !maps ||
      !pin ||
      !lookupPin ||
      !getLookupGhost(pin, lookupPin)
    ) {
      connectorRef.current?.setMap(null);
      connectorRef.current = null;
      return;
    }

    const path = [
      { lat: pin.latitude, lng: pin.longitude },
      { lat: lookupPin.latitude, lng: lookupPin.longitude },
    ];

    if (connectorRef.current) {
      connectorRef.current.setMap(map);
      connectorRef.current.setPath(path);
      return;
    }

    connectorRef.current = new maps.Polyline({
      geodesic: true,
      icons: [
        {
          icon: {
            path: 'M 0,-1 0,1',
            scale: 4,
            strokeColor: CONNECTOR_OFF_WHITE,
            strokeOpacity: 1,
            strokeWeight: 3,
          },
          offset: '0',
          repeat: '16px',
        },
      ],
      map,
      path,
      strokeColor: CONNECTOR_OFF_WHITE,
      strokeOpacity: 0,
      strokeWeight: 2,
    });
  }, [lookupPin, mapLoadStatus, pin]);

  useEffect(
    () => () => {
      activeMarkerListenersRef.current.forEach((listener) =>
        listener.remove?.()
      );
      activeMarkerDragRef.current = null;
      activeMarkerRef.current?.setMap(null);
      lookupMarkerRef.current?.setMap(null);
      connectorRef.current?.setMap(null);
    },
    []
  );

  const isMapReady = mapLoadStatus === 'ready';
  const markerDescription = (() => {
    if (!pin) return 'No location is marked on the map.';
    if (getLookupGhost(pin, lookupPin)) {
      return 'The active location and the address lookup location are marked on the map.';
    }
    return 'The active location is marked on the map.';
  })();
  const mapLoadMessage = (() => {
    if (mapLoadStatus !== 'error') return 'Loading the site location map.';
    return editable
      ? 'The site location map could not load. Pin placement is unavailable.'
      : 'The site location map could not load.';
  })();

  return (
    <>
      {isMapReady && (
        <div className="sr-only">
          <Text id="site-location-editor-map-markers">{markerDescription}</Text>
        </div>
      )}
      <div
        // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Viewport-relative size
        className="relative h-[min(70vh,42rem)] min-h-72 w-full overflow-hidden rounded-lg border border-gray-200"
        data-testid="site-location-editor-map"
        role="region"
        aria-label="Site location map"
        aria-describedby={
          isMapReady ? 'site-location-editor-map-markers' : undefined
        }
        aria-disabled={disabled || !isMapReady}
      >
        <GoogleMapReact
          bootstrapURLKeys={{ key: GOOGLE_MAPS_KEY || '' }}
          center={{ lat: centre.latitude, lng: centre.longitude }}
          defaultCenter={{
            lat: centre.latitude,
            lng: centre.longitude,
          }}
          defaultZoom={19}
          options={{
            draggable: !disabled,
            fullscreenControl: true,
            gestureHandling: disabled ? 'none' : 'auto',
            keyboardShortcuts: true,
            mapTypeControl: false,
            mapTypeId: 'hybrid',
            maxZoom: 21,
            minZoom: 3,
            streetViewControl: false,
            zoomControl: true,
          }}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => {
            if (!map || !maps) {
              mapRef.current = null;
              mapsRef.current = null;
              setMapLoadStatus('error');
              return;
            }

            mapRef.current = map as MapInstance;
            mapsRef.current = maps as GoogleMapsApi;
            setMapLoadStatus('ready');
          }}
        />
        {!isMapReady && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 p-4 text-center"
            role={mapLoadStatus === 'error' ? 'alert' : 'status'}
            aria-live={mapLoadStatus === 'error' ? 'assertive' : 'polite'}
          >
            <Text as="span">{mapLoadMessage}</Text>
          </div>
        )}
      </div>
    </>
  );
};

export default SiteLocationEditorMap;
